import type { DrawingDocument } from '../model';
import type { EditorSelection } from '../domain/editor';
import { alignableObjectsInSelection } from '../domain/objectAlignment';
import { DEFAULT_BOND_DRAWING_CONFIG } from '../domain/bonds';
import { changeDepiction } from '../domain/drawingDocumentCore';
import { rdkitMoleculeForLayout } from './chemicalValidation';
import { parseMolBlockGeometry } from './smilesImport';
import { loadRdKit } from './rdkitAdapter';

/** Layout only complete selected components. Stable IDs retain styles, roles and attachments. */
export async function cleanUpStructures(
  document: DrawingDocument,
  selection: EditorSelection
): Promise<DrawingDocument> {
  const components = alignableObjectsInSelection(document, selection).filter(
    (object) => object.kind === 'molecule' && object.atomIds.length > 1
  );
  if (!components.length) return document;
  const rdkit = await loadRdKit();
  const source = document.depiction.coordinateSet;
  const coordinates = { ...source.coordinates };
  const depths = { ...source.depths };
  const bondStyles = { ...document.depiction.bondStyles };
  const overrides = { ...document.depiction.bondLengthOverrides };
  for (const component of components) {
    if (component.kind !== 'molecule') continue;
    const ids = new Set(component.atomIds);
    const nodes = document.structure.graph.nodes.filter((node) => ids.has(node.id));
    const edges = document.structure.graph.edges.filter((edge) => ids.has(edge.source));
    const fragment: DrawingDocument = {
      ...document,
      structure: { ...document.structure, graph: { ...document.structure.graph, nodes, edges } }
    };
    const molecule = rdkitMoleculeForLayout(rdkit, fragment);
    if (!molecule) throw new Error('The selected structure could not be laid out.');
    try {
      if (!molecule.is_valid()) throw new Error('The selected structure could not be laid out.');
      const geometry = parseMolBlockGeometry(molecule.get_new_coords(), nodes.length);
      // Reaction-box membership uses the bounding-box centre. Keeping it fixed also keeps
      // a tidied participant in its current role, even when an asymmetric layout changes.
      const centerOf = (points: readonly { x: number; y: number }[]) => ({
        x: (Math.min(...points.map((p) => p.x)) + Math.max(...points.map((p) => p.x))) / 2,
        y: (Math.min(...points.map((p) => p.y)) + Math.max(...points.map((p) => p.y))) / 2
      });
      const center = centerOf(nodes.map((node) => source.coordinates[node.id]!));
      const rawCenter = centerOf(geometry.coordinates);
      // RDKit's standard bond is 1.5 units; the editor's import/drawing standard is 45 units.
      for (let index = 0; index < nodes.length; index++) {
        const id = nodes[index]!.id,
          point = geometry.coordinates[index]!;
        coordinates[id] = {
          x: center.x + (point.x - rawCenter.x) * (DEFAULT_BOND_DRAWING_CONFIG.lengthPx / 1.5),
          y: center.y - (point.y - rawCenter.y) * (DEFAULT_BOND_DRAWING_CONFIG.lengthPx / 1.5)
        };
        delete depths[id];
      }
      for (const edge of edges) delete overrides[edge.id];
      let candidate: DrawingDocument = {
        ...fragment,
        depiction: { ...document.depiction, bondStyles, coordinateSet: { ...source, coordinates } }
      };
      // Keep the user's chosen wedge bond. Reverse its sense only when the new arrangement
      // reverses the atom's perceived parity. Comparing RDKit's atom order is ID-independent.
      const before = JSON.parse(molecule.get_json()) as { molecules: { atoms: { stereo?: string }[] }[] };
      let check = rdkitMoleculeForLayout(rdkit, candidate);
      if (!check) throw new Error('Could not verify the cleaned structure.');
      try {
        const after = JSON.parse(check.get_json()) as typeof before;
        for (let index = 0; index < nodes.length; index++) {
          const original = before.molecules[0]!.atoms[index]!.stereo;
          if (!original || original === 'unspecified' || original === after.molecules[0]!.atoms[index]!.stereo)
            continue;
          for (const edge of edges) {
            const style = bondStyles[edge.id];
            if (
              style &&
              (style.style === 'solid-wedge' || style.style === 'hashed-wedge') &&
              style.fromAtomId === nodes[index]!.id
            )
              bondStyles[edge.id] = { ...style, style: style.style === 'solid-wedge' ? 'hashed-wedge' : 'solid-wedge' };
          }
        }
      } finally {
        check.delete();
      }
      candidate = { ...candidate, depiction: { ...candidate.depiction, bondStyles } };
      check = rdkitMoleculeForLayout(rdkit, candidate);
      try {
        if (!check || check.get_smiles() !== molecule.get_smiles())
          throw new Error('Clean up could not preserve stereochemistry; the drawing was left unchanged.');
      } finally {
        check?.delete();
      }
    } finally {
      molecule.delete();
    }
  }
  return changeDepiction(document, {
    coordinateSet: { ...source, coordinates, ...(source.depths ? { depths } : {}) },
    bondStyles,
    bondLengthOverrides: overrides
  });
}
